/**
 This file is used for controlling the global states of the components,
 you can customize the states for the different components here.
 */

import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';
import { ThemeProvider } from '@mui/material/styles';
import colors from 'assets/theme/base/colors';
import { useThemeBuilder } from 'hook/useThemeBuilder';

// Material Dashboard 2 React main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = 'MaterialUIContext';

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case 'MINI_SIDENAV': {
      return { ...state, miniSidenav: action.value };
    }
    case 'TRANSPARENT_SIDENAV': {
      return { ...state, transparentSidenav: action.value };
    }
    case 'WHITE_SIDENAV': {
      return { ...state, whiteSidenav: action.value };
    }
    case 'SIDENAV_COLOR': {
      return { ...state, sidenavColor: action.value };
    }
    case 'TRANSPARENT_NAVBAR': {
      return { ...state, transparentNavbar: action.value };
    }
    case 'FIXED_NAVBAR': {
      return { ...state, fixedNavbar: action.value };
    }
    case 'OPEN_CONFIGURATOR': {
      return { ...state, openConfigurator: action.value };
    }
    case 'DIRECTION': {
      return { ...state, direction: action.value };
    }
    case 'LAYOUT': {
      return { ...state, layout: action.value };
    }
    case 'DARKMODE': {
      return {
        ...state,
        darkMode: action.value,
        transparentSidenav: false,
        whiteSidenav: !action.value
      };
    }
    case 'COLOR_PRIMARY': {
      return { ...state, primaryColor: action.value };
    }
    case 'COLOR_SECONDARY': {
      return { ...state, secondaryColor: action.value };
    }
    case 'RESET': {
      return { ...JSON.parse(localStorage.getItem('MaterialUIControllerInitialState')) };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const localStorageKey = 'MaterialUIController';

  const [pageTitle, setPageTitle] = useState(null);

  const initialState = useMemo(
    () => ({
      miniSidenav: true,
      transparentSidenav: false,
      whiteSidenav: true,
      sidenavColor: 'info',
      transparentNavbar: false,
      fixedNavbar: true,
      openConfigurator: false,
      direction: 'ltr',
      layout: 'dashboard',
      darkMode: false,
      primaryColor: colors.primary.main,
      secondaryColor: colors.secondary.main
    }),
    []
  );

  // Setting the initial UI properties from configuration
  useEffect(() => {
    localStorage.setItem(`${localStorageKey}InitialState`, JSON.stringify(initialState));
  }, [initialState]);

  const initializer = (initialValue) =>
    JSON.parse(localStorage.getItem(localStorageKey)) || initialValue;

  const [controller, dispatch] = useReducer(reducer, initialState, initializer);
  const { darkMode, primaryColor, secondaryColor } = controller;

  const themeBuilder = useThemeBuilder(primaryColor, secondaryColor);
  const customTheme = !darkMode ? themeBuilder.lightTheme : themeBuilder.darkTheme;

  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(controller));
  }, [controller]);

  const value = useMemo(
    () => ({
      controller,
      dispatch,
      pageTitle,
      setPageTitle
    }),
    [controller, dispatch, pageTitle]
  );

  return (
    <MaterialUI.Provider value={value}>
      <ThemeProvider theme={customTheme}>{children}</ThemeProvider>
    </MaterialUI.Provider>
  );
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired
};

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      'useMaterialUIController should be used inside the MaterialUIControllerProvider.'
    );
  }

  return context;
}

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: 'MINI_SIDENAV', value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: 'TRANSPARENT_SIDENAV', value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: 'WHITE_SIDENAV', value });
const setSidenavColor = (dispatch, value) => dispatch({ type: 'SIDENAV_COLOR', value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: 'TRANSPARENT_NAVBAR', value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: 'FIXED_NAVBAR', value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: 'OPEN_CONFIGURATOR', value });
const setDirection = (dispatch, value) => dispatch({ type: 'DIRECTION', value });
const setLayout = (dispatch, value) => dispatch({ type: 'LAYOUT', value });
const setDarkMode = (dispatch, value) => dispatch({ type: 'DARKMODE', value });
const setPrimaryColor = (dispatch, value) => dispatch({ type: 'COLOR_PRIMARY', value });
const setSecondaryColor = (dispatch, value) => dispatch({ type: 'COLOR_SECONDARY', value });
const resetTheme = (dispatch) => dispatch({ type: 'RESET' });

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setPrimaryColor,
  setSecondaryColor,
  resetTheme
};
